import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { ExploreImageView } from '@vizit/api';
import {
  type ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetData,
  type ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetError,
  type ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetResponse,
  listClassificationImagesV1ExploreClassificationsClassificationIdImagesGet,
} from '../../generated';

export type ListClassificationImagesQueryVariables =
  ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetData['path'] &
    ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetData['query'];

export type ListClassificationImagesQuery = {
  data: ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useListClassificationImagesQuery = <
  TData = ListClassificationImagesQuery,
  TError = ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetError,
>(
  {
    classification_id,
    limit,
    offset,
    sort,
    product_category_id,
    query_id,
    min_score,
    max_score,
  }: ListClassificationImagesQueryVariables,
  options?: Omit<UseQueryOptions<ListClassificationImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListClassificationImagesQuery, TError, TData>({
    queryKey: [
      'ListClassificationImages',
      { classification_id, limit, offset, sort, product_category_id, query_id, min_score, max_score },
    ],
    queryFn: async () => {
      const { data, response } = await listClassificationImagesV1ExploreClassificationsClassificationIdImagesGet({
        path: { classification_id },
        query: {
          limit,
          offset,
          sort,
          product_category_id,
          query_id,
          min_score: min_score ? Number(min_score) : undefined,
          max_score: max_score ? Number(max_score) : undefined,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useListClassificationImagesQuery.getKey = (variables: ListClassificationImagesQueryVariables) => [
  'ListClassificationImages',
  variables,
];

useListClassificationImagesQuery.fetcher = (
  {
    classification_id,
    limit,
    offset,
    sort,
    product_category_id,
    query_id,
    min_score,
    max_score,
  }: ListClassificationImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await listClassificationImagesV1ExploreClassificationsClassificationIdImagesGet({
      path: { classification_id },
      query: {
        limit,
        offset,
        sort,
        product_category_id,
        query_id,
        min_score: Number(min_score),
        max_score: Number(max_score),
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type ListClassificationImagesInfiniteQueryVariables = Omit<ListClassificationImagesQueryVariables, 'offset'>;

export const useListClassificationImagesInfiniteQuery = <
  TData = ListClassificationImagesQuery,
  TError = ListClassificationImagesV1ExploreClassificationsClassificationIdImagesGetError,
>(
  {
    classification_id,
    limit,
    sort,
    product_category_id,
    query_id,
    min_score,
    max_score,
  }: ListClassificationImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<ListClassificationImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<ListClassificationImagesQuery, TError, TData>({
    queryKey: [
      'ListClassificationImagesInfinite',
      { classification_id, limit, sort, product_category_id, query_id, min_score, max_score },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await listClassificationImagesV1ExploreClassificationsClassificationIdImagesGet({
        path: { classification_id },
        query: {
          limit,
          offset: pageParam as number,
          sort,
          product_category_id,
          query_id,
          min_score: min_score ? Number(min_score) : undefined,
          max_score: max_score ? Number(max_score) : undefined,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: (
      query: Query<ListClassificationImagesQuery, TError, InfiniteData<ListClassificationImagesQuery, unknown>>,
    ) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 100;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // Check all pages for images without scores or URLs
      const allImages = query.state.data?.pages?.flatMap((page: ListClassificationImagesQuery) => page.data) || [];
      const imagesDoNotHaveVizitScore = allImages.some((image: ExploreImageView) => image.score === null);
      const imagesDoNotHaveUrl = allImages.some(
        (image: ExploreImageView) => image.image_path === null || !image.image_path?.includes('/images'),
      );

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && !maxRetriesReached) {
        return 1000 * (query.state.dataUpdateCount + 1);
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useListClassificationImagesInfiniteQuery.getKey = (variables: ListClassificationImagesInfiniteQueryVariables) => [
  'ListClassificationImagesInfinite',
  variables,
];

useListClassificationImagesInfiniteQuery.fetcher = (
  {
    classification_id,
    limit,
    sort,
    product_category_id,
    query_id,
    min_score,
    max_score,
  }: ListClassificationImagesInfiniteQueryVariables,
  offset = 0,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await listClassificationImagesV1ExploreClassificationsClassificationIdImagesGet({
      path: { classification_id },
      query: {
        limit,
        offset,
        sort,
        product_category_id,
        query_id,
        min_score: min_score ? Number(min_score) : undefined,
        max_score: max_score ? Number(max_score) : undefined,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
